/**
 * TSM Group Ranking Report Renderer
 *
 * Service Advisor leaderboard split into four sections — Domestic, Import,
 * Express, and Unassigned — each ranked independently. Designed for landscape
 * printing with one section per page so each group can be posted separately.
 *
 * Sub-components (`MetricLeaders`, `RankedTable`, `LocationAggregateTable`,
 * formatters) live in `TsmRankingShared.tsx` and are shared with the
 * company-wide `TsmRankingReport.tsx`.
 */

import React, { useMemo } from 'react';
import { Squares2X2Icon, FlagIcon, BoltIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import {
  fmtAsOf,
  LocationAggregateTable,
  MetricLeader,
  MetricLeaders,
  MultiLocationExcludedAdvisor,
  RankedTable,
  TsmLocationRow,
  TsmRankedRow,
} from './TsmRankingShared';

export type TsmGroupKey = 'Domestic' | 'Import' | 'Express' | 'Unassigned';

export interface TsmGroupSection {
  group: TsmGroupKey;
  totalAdvisors: number;
  podium: TsmRankedRow[];
  metricLeaders: MetricLeader[];
  rows: TsmRankedRow[];
}

export interface TsmGroupRankingReportData {
  reportMonth: { year: number; month: number; monthName: string };
  asOf: string | null;
  totalAdvisors: number;
  groups: {
    domestic: TsmGroupSection;
    import: TsmGroupSection;
    express: TsmGroupSection;
    unassigned: TsmGroupSection;
  };
  multiLocationExcluded?: MultiLocationExcludedAdvisor[];
  locationRows?: TsmLocationRow[];
}

// Visual + label config per group. Domestic / Import / Express get distinct
// colors so the printed pages are visually distinguishable at a glance on a
// bulletin board.
const GROUP_VISUALS: Record<TsmGroupKey, {
  label: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  accentRingClass: string;
  badgeClass: string;
  headingIconBg: string;
  headingIconText: string;
}> = {
  Domestic: {
    label: 'Domestic',
    Icon: FlagIcon,
    accentRingClass: 'ring-blue-200',
    badgeClass: 'bg-blue-50 text-blue-800 ring-1 ring-blue-200',
    headingIconBg: 'bg-blue-100',
    headingIconText: 'text-blue-700',
  },
  Import: {
    label: 'Import',
    Icon: Squares2X2Icon,
    accentRingClass: 'ring-emerald-200',
    badgeClass: 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200',
    headingIconBg: 'bg-emerald-100',
    headingIconText: 'text-emerald-700',
  },
  Express: {
    label: 'Express',
    Icon: BoltIcon,
    accentRingClass: 'ring-orange-200',
    badgeClass: 'bg-orange-50 text-orange-800 ring-1 ring-orange-200',
    headingIconBg: 'bg-orange-100',
    headingIconText: 'text-orange-700',
  },
  Unassigned: {
    label: 'Unassigned',
    Icon: QuestionMarkCircleIcon,
    accentRingClass: 'ring-gray-200',
    badgeClass: 'bg-gray-100 text-gray-700 ring-1 ring-gray-200',
    headingIconBg: 'bg-gray-100',
    headingIconText: 'text-gray-600',
  },
};

interface GroupSectionViewProps {
  section: TsmGroupSection;
  pageBreakBefore: boolean;
}

const GroupSectionView: React.FC<GroupSectionViewProps> = ({ section, pageBreakBefore }) => {
  const visuals = GROUP_VISUALS[section.group];
  const Icon = visuals.Icon;
  const isUnassigned = section.group === 'Unassigned';

  return (
    <section
      className={`mb-10 ${pageBreakBefore ? 'print:break-before-page' : ''}`}
    >
      <div className={`flex items-center justify-between mb-3 print:mb-2`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${visuals.headingIconBg}`}>
            <Icon className={`h-5 w-5 ${visuals.headingIconText}`} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 print:text-lg">
              {visuals.label}
              <span className={`ml-3 text-xs font-medium align-middle px-2 py-0.5 rounded ${visuals.badgeClass}`}>
                {section.totalAdvisors} {section.totalAdvisors === 1 ? 'advisor' : 'advisors'}
              </span>
            </h2>
            {isUnassigned && section.totalAdvisors > 0 && (
              <p className="text-xs text-amber-700 mt-0.5 italic">
                These advisors don't have a group assigned yet. Visit
                {' '}<span className="font-semibold">Configuration → TSM Groups</span> to assign them.
              </p>
            )}
          </div>
        </div>
      </div>

      {section.totalAdvisors === 0 ? (
        <div className="text-sm text-gray-500 italic px-3 py-6 bg-gray-50 border border-gray-100 rounded">
          No advisors {section.group === 'Unassigned'
            ? 'are currently unassigned (all eligible TSMs have a group set).'
            : `assigned to the ${visuals.label} group yet.`}
        </div>
      ) : (
        <>
          <MetricLeaders leaders={section.metricLeaders} />
          <RankedTable rows={section.rows} />
        </>
      )}
    </section>
  );
};

export const TsmGroupRankingReport: React.FC<{ data: TsmGroupRankingReportData }> = ({ data }) => {
  const tsmRowsByLocation = useMemo(() => {
    const map = new Map<number, TsmRankedRow[]>();
    if (data?.groups) {
      // Use the unassigned-excluded company-wide ranking for the per-location
      // drill-down: each location's expandable row should show its TSMs sorted
      // by THEIR most relevant group ranking. We pool all groups' rows back
      // together for the location-level drilldown (the table is per-location,
      // not per-group, so cross-group ordering doesn't really make sense — we
      // just keep them sorted by raw totalScore within each location).
      const allRows = [
        ...data.groups.domestic.rows,
        ...data.groups.import.rows,
        ...data.groups.express.rows,
        ...data.groups.unassigned.rows,
      ];
      for (const r of allRows) {
        const list = map.get(r.locationId) ?? [];
        list.push(r);
        map.set(r.locationId, list);
      }
      map.forEach((list) => {
        list.sort((a, b) => a.totalScore - b.totalScore);
      });
    }
    return map;
  }, [data?.groups]);

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500">
        No data available for this period.
      </div>
    );
  }

  if (data.totalAdvisors === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No Service Advisor activity found for {data.reportMonth.monthName} {data.reportMonth.year}.
      </div>
    );
  }

  return (
    <div className="text-gray-900">
      {/* Header */}
      <div className="mb-6 print:mb-4 print:break-inside-avoid">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 print:text-xl">TSM Group Ranking Report</h1>
            <p className="text-sm text-gray-600 mt-1">
              {data.reportMonth.monthName} {data.reportMonth.year} · {data.totalAdvisors} Service Advisors split into Domestic / Import / Express
            </p>
          </div>
          <div className="text-xs text-gray-500">
            Snapshot as of {fmtAsOf(data.asOf)}
          </div>
        </div>
      </div>

      {/* Four group sections — each on its own printed page */}
      <GroupSectionView section={data.groups.domestic} pageBreakBefore={false} />
      <GroupSectionView section={data.groups.import} pageBreakBefore />
      <GroupSectionView section={data.groups.express} pageBreakBefore />
      <GroupSectionView section={data.groups.unassigned} pageBreakBefore />

      {/* Per-location aggregate (unchanged from company-wide report) */}
      {data.locationRows && data.locationRows.length > 0 && (
        <div className="mt-10 print:mt-8 print:break-before-page">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            By Location <span className="text-gray-400 normal-case font-normal">(click a row to expand its TSMs)</span>
          </div>
          <LocationAggregateTable
            locationRows={data.locationRows}
            tsmRowsByLocation={tsmRowsByLocation}
            tsmTotal={data.totalAdvisors}
          />
        </div>
      )}

      {/* Multi-location fill-in footnote */}
      {data.multiLocationExcluded && data.multiLocationExcluded.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 leading-relaxed print:break-inside-avoid">
          <strong className="text-gray-700">Excluded — multi-location fill-in advisors:</strong>{' '}
          {data.multiLocationExcluded
            .map((a) => `${a.advisorName} (covered ${a.locations.join(', ')})`)
            .join('; ')}
          .{' '}
          <span className="italic">
            These TSMs wrote ROs at 2+ locations this month — typically covering vacations across the
            company — so they're removed from the head-to-head rankings.
          </span>
        </div>
      )}

      {/* Methodology */}
      <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 leading-relaxed print:break-inside-avoid">
        <strong className="text-gray-700">How scoring works:</strong> Each group is ranked independently —
        a TSM ranked #1 in <em>Domestic</em> is "best Domestic TSM," not necessarily best company-wide.
        Advisors are ranked 1..N inside each group on six metrics (lower rank = better). Standard
        competition ranking — ties share the lower rank, the next advisor jumps the gap (1, 2, 2, 4). The
        Total Score sums <em>all six</em> ranks; <em>lower score wins</em>. The Closed/Open ROs column
        shows closed and open RO counts (e.g. 100/33) and is ranked by fewest open ROs. Metrics: Open ROs
        (fewer open, scored), Oldest Open RO Age (younger, scored), Customer Pay + Service Contract Gross
        (higher, scored), Avg Flat-Rate Hours per Customer Pay RO (higher, scored), Total Labor Gross all
        pay types (higher, scored), Customer Pay ELR (higher, scored). Locations 12 and 44 are Body Shop
        and excluded. To qualify, an
        advisor must have written at least 1 RO (open or closed) per Service working day of the month
        at their location — e.g. on working day 16 they need ≥ 16 ROs; for a completed past month
        they need ≥ the month's full working-day count. Loc 5 &amp; Loc 11 are Service-closed on
        Saturdays so their threshold is lower than the other six. This drops assistants, occasional
        RO writers, and new hires with too little activity to compete. Group assignments are managed
        in <span className="font-semibold">Configuration → TSM Groups</span>.
        {' '}The <em>By Location</em> table at the bottom is shared across all groups. Its <em>Avg Hrs/RO (CP+SC)</em>
        column is sourced directly from the GL (450* labor sales accounts, per-location filter) — true store-wide pool of
        CP+SC hours ÷ distinct ROs that posted CP/SC labor revenue. Loc 1's CVC accounts are included (internal service
        center); Loc 4's real body shop is excluded.
      </div>
    </div>
  );
};
